import { BleSetupError, delay, isBase64url, type DeviceBleTransport, type ProvisioningEnvelope, type ProvisioningInfo, type ReceiptStatus } from './transport.js';
import type { ProvisioningProgress } from './provision.js';

export type InitialNetwork = { ssid: string; psk: string } | { reuse: true };
/** A unit showing a code on its screen (ASIS One) can also take networks from the account vault. */
export type ScreenNetwork = InitialNetwork | { networkId: string } | { networkIds: string[] };
/** Factory label proof, or the code read off the unit's screen (never sent: only its HMAC proof is). */
export type Possession = { proof: string } | { code: string };
export interface InitialPairingApi {
  initialPairingStart(id: string, body: { nonce: string; proof: string } & InitialNetwork, signal?: AbortSignal): Promise<{ claimId: string; envelope: ProvisioningEnvelope }>;
  screenPairingStart(id: string, body: { nonce: string; codeProof: string; codeCheck: string } & ScreenNetwork, signal?: AbortSignal): Promise<{ claimId: string; envelope: ProvisioningEnvelope }>;
  initialPairingFinish(id: string, body: { claimId: string; nonce: string; status: ReceiptStatus; mac: string }, signal?: AbortSignal): Promise<{ verified: boolean; status: ReceiptStatus; claimed: boolean }>;
  initialPairingCancel(id: string, body: { claimId: string; nonce: string }, signal?: AbortSignal): Promise<{ cancelled: boolean }>;
  listDevices(signal?: AbortSignal): Promise<{ devices: Array<{ id: string; status: string; lastSeenAt: string | null }> }>;
}
/** A label is data, not a URL. Never navigate or persist it. */
export function parsePossessionCode(text: string, expectedId: string): string | null {
  let value = text.trim();
  const prefix = 'ASIS-SETUP-V1:';
  if (value.startsWith(prefix)) {
    const payload = value.slice(prefix.length), separator = payload.lastIndexOf(':');
    if (separator < 0 || payload.slice(0, separator) !== expectedId) return null;
    value = payload.slice(separator + 1);
  }
  return isBase64url(value, 32) ? value : null;
}

export const SCREEN_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
export const SCREEN_CODE_LENGTH = 6;
/** What the person typed → the code as the unit shows it (spaces, dashes and case ignored; O/I/0/1 never appear). */
export function normalizeScreenCode(text: string): string | null {
  const value = text.replace(/[\s-]/gu, '').toUpperCase();
  return value.length === SCREEN_CODE_LENGTH && [...value].every(char => SCREEN_CODE_ALPHABET.includes(char)) ? value : null;
}
const toBase64url = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes)).replace(/\+/gu, '-').replace(/\//gu, '_').replace(/=+$/u, '');
/** HMAC-SHA256(key = code, "ASIS-SCREEN-CODE-PROOF-V1\n" + id + "\n" + nonce): same as the firmware and the server. */
export async function screenCodeProof(code: string, id: string, nonce: string): Promise<string> {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey('raw', encoder.encode(code), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return toBase64url(new Uint8Array(await crypto.subtle.sign('HMAC', key, encoder.encode(`ASIS-SCREEN-CODE-PROOF-V1\n${id}\n${nonce}`))));
}

export async function provisionInitialDevice(options: {
  api: InitialPairingApi; transport: Pick<DeviceBleTransport, 'send' | 'disconnect'>; info: ProvisioningInfo;
  network: ScreenNetwork; possession: Possession; signal: AbortSignal;
  onProgress: (state: ProvisioningProgress) => void;
  now?: () => number; wait?: (ms: number, signal: AbortSignal) => Promise<void>; cloudTimeoutMs?: number;
}): Promise<{ saved: boolean; cloudConfirmed: boolean; status: ReceiptStatus }> {
  const { api, info, signal, transport } = options;
  const now = options.now ?? (() => performance.now());
  let claimId: string | null = null, receiptReceived = false, completed = false;
  const check = () => { if (signal.aborted) throw new BleSetupError('cancelled'); };
  try {
    check(); options.onProgress('sending');
    if (info.owned) throw new BleSetupError('account_changed');
    let result: { claimId: string; envelope: ProvisioningEnvelope };
    if ('code' in options.possession) {
      const code = normalizeScreenCode(options.possession.code); options.possession.code = '';
      if (!code || !info.codeCheck) throw new BleSetupError('wrong_code');
      const codeProof = await screenCodeProof(code, info.id, info.nonce); check();
      try { result = await api.screenPairingStart(info.id, { nonce: info.nonce, codeProof, codeCheck: info.codeCheck, ...options.network }, signal); }
      catch (error) {
        // 403 here means the proof did not match the screen: say so instead of a generic refusal.
        if ((error as { status?: unknown })?.status === 403) throw new BleSetupError('wrong_code');
        throw error;
      }
    } else {
      if ('networkId' in options.network || 'networkIds' in options.network) throw new BleSetupError('invalid_info');
      result = await api.initialPairingStart(info.id, { nonce: info.nonce, proof: options.possession.proof, ...options.network }, signal);
      options.possession.proof = '';
    }
    claimId = result.claimId;
    if ('psk' in options.network) options.network.psk = '';
    check();
    const receipt = await transport.send(result.envelope, signal); receiptReceived = true;
    check(); options.onProgress('verifying');
    // One idempotent retry recovers a lost finish response. ApiClient still
    // checks account generation and abort, so it cannot cross a logout.
    const request = { claimId, nonce: info.nonce, status: receipt.status, mac: receipt.mac };
    let verified;
    try { verified = await api.initialPairingFinish(info.id, request, signal); }
    catch (error) {
      check();
      // Explicit HTTP rejection is authoritative; retry only transport failures.
      if (typeof (error as { status?: unknown })?.status === 'number') throw error;
      verified = await api.initialPairingFinish(info.id, request, signal);
    }
    check();
    if (!verified.verified || verified.status !== receipt.status || verified.claimed !== (receipt.status === 'saved')) throw new BleSetupError('invalid_receipt');
    completed = verified.claimed;
    transport.disconnect();
    if (!completed) return { saved: false, cloudConfirmed: false, status: receipt.status };
    // A heartbeat before assignment cannot prove this owner's cloud binding.
    // Establish a server baseline AFTER verified ownership, then require newer contact.
    const { devices: before } = await api.listDevices(signal); check();
    const baseline = before.find(device => device.id === info.id && device.status !== 'revoked');
    if (!baseline) throw new BleSetupError('account_changed');
    const seen = baseline.lastSeenAt === null ? Number.NEGATIVE_INFINITY : Date.parse(baseline.lastSeenAt);
    options.onProgress('waiting_cloud');
    const end = now() + (options.cloudTimeoutMs ?? 90_000);
    do {
      check(); const { devices } = await api.listDevices(signal); check();
      const device = devices.find(item => item.id === info.id && item.status !== 'revoked');
      if (device?.lastSeenAt && Date.parse(device.lastSeenAt) > seen) return { saved: true, cloudConfirmed: true, status: 'saved' };
      if (now() >= end) break;
      await (options.wait ?? delay)(3000, signal);
    } while (now() <= end);
    return { saved: true, cloudConfirmed: false, status: 'saved' };
  } finally {
    if ('code' in options.possession) options.possession.code = ''; else options.possession.proof = '';
    if ('psk' in options.network) options.network.psk = '';
    // Before BLE acknowledgement a cancellation cannot unclaim anything. Once
    // a receipt arrived, preserve the recovery path if finish response was lost.
    if (claimId && !receiptReceived && !completed && !signal.aborted) {
      try { await api.initialPairingCancel(info.id, { claimId, nonce: info.nonce }, signal); } catch { /* bounded reservation expires */ }
    }
    transport.disconnect();
  }
}
